/**
 * Windows Desktop Redux Slice
 * Manages state for Windows desktop sessions
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  listWindowsDesktops,
  createWindowsDesktop,
  getWindowsDesktop,
  deleteWindowsDesktop,
  windowsDesktopAction,
  getWindowsCredentials
} from "../api/windowsApi";

// Async thunks
export const fetchWindowsDesktops = createAsyncThunk(
  "windows/fetchDesktops",
  async (_, { rejectWithValue }) => {
    try {
      const response = await listWindowsDesktops();
      return response.items || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const launchWindowsDesktop = createAsyncThunk(
  "windows/launchDesktop",
  async (params, { rejectWithValue }) => {
    try {
      const response = await createWindowsDesktop(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchWindowsDesktop = createAsyncThunk(
  "windows/fetchDesktop",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getWindowsDesktop(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const removeWindowsDesktop = createAsyncThunk(
  "windows/removeDesktop",
  async (id, { rejectWithValue }) => {
    try {
      await deleteWindowsDesktop(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const performWindowsAction = createAsyncThunk(
  "windows/performAction",
  async ({ id, action }, { rejectWithValue }) => {
    try {
      const response = await windowsDesktopAction(id, action);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchWindowsCredentials = createAsyncThunk(
  "windows/fetchCredentials",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getWindowsCredentials(id);
      return { id, credentials: response };
    } catch (error) {
    const status = error.response?.status;
    const message = error.response?.data?.error || error.response?.data?.message || error.message;
    // Mark 409 as a transient, provisioning-in-progress condition so the
    // viewer can keep polling without treating it as a hard error.
    if (status === 409) {
    return rejectWithValue({ transient: true, status, message });
    }
    return rejectWithValue({ transient: false, status, message });
    }
  }
);

const initialState = {
  desktops: [],
  activeDesktop: null,
  credentials: {},
  loading: false,
  error: null,
  actionLoading: {}, // keyed by desktop ID — true while an action is in-flight
  launchStatus: "idle", // idle, loading, succeeded, failed
  connectionStatus: "disconnected" // disconnected, connecting, connected, error
};

const windowsSlice = createSlice({
  name: "windows",
  initialState,
  reducers: {
    setActiveWindowsDesktop: (state, action) => {
      state.activeDesktop = action.payload;
    },
    clearActiveWindowsDesktop: (state) => {
      state.activeDesktop = null;
    },
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateDesktopInList: (state, action) => {
      const index = state.desktops.findIndex((d) => d.id === action.payload.id);
      if (index !== -1) {
        state.desktops[index] = action.payload;
      }
      if (state.activeDesktop?.id === action.payload.id) {
        state.activeDesktop = action.payload;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all desktops
      .addCase(fetchWindowsDesktops.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWindowsDesktops.fulfilled, (state, action) => {
        state.loading = false;
        state.desktops = action.payload;
      })
      .addCase(fetchWindowsDesktops.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Launch desktop
      .addCase(launchWindowsDesktop.pending, (state) => {
        state.launchStatus = "loading";
        state.error = null;
      })
      .addCase(launchWindowsDesktop.fulfilled, (state, action) => {
        state.launchStatus = "succeeded";
        state.desktops.push(action.payload);
        state.activeDesktop = action.payload;
      })
      .addCase(launchWindowsDesktop.rejected, (state, action) => {
        state.launchStatus = "failed";
        state.error = action.payload;
      })

      // Fetch single desktop
      .addCase(fetchWindowsDesktop.fulfilled, (state, action) => {
        const index = state.desktops.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.desktops[index] = action.payload;
        } else {
          state.desktops.push(action.payload);
        }
        if (state.activeDesktop?.id === action.payload.id) {
          state.activeDesktop = action.payload;
        }
      })

      // Remove desktop
      .addCase(removeWindowsDesktop.fulfilled, (state, action) => {
        state.desktops = state.desktops.filter((d) => d.id !== action.payload);
        if (state.activeDesktop?.id === action.payload) {
          state.activeDesktop = null;
        }
      })

      // Perform action
      .addCase(performWindowsAction.pending, (state, action) => {
        const id = action.meta.arg.id;
        state.actionLoading[id] = true;
      })
      .addCase(performWindowsAction.fulfilled, (state, action) => {
        const id = action.payload.id;
        delete state.actionLoading[id];
        const index = state.desktops.findIndex((d) => d.id === id);
        if (index !== -1) {
          state.desktops[index] = action.payload;
        }
        if (state.activeDesktop?.id === id) {
          state.activeDesktop = action.payload;
        }
      })
      .addCase(performWindowsAction.rejected, (state, action) => {
        const id = action.meta.arg.id;
        delete state.actionLoading[id];
      })

      // Fetch credentials
      .addCase(fetchWindowsCredentials.fulfilled, (state, action) => {
        state.credentials[action.payload.id] = action.payload.credentials;
      });
  }
});

export const {
  setActiveWindowsDesktop,
  clearActiveWindowsDesktop,
  setConnectionStatus,
  clearError,
  updateDesktopInList
} = windowsSlice.actions;

export default windowsSlice.reducer;
